import {
  Alert,
  Button,
  Stack,
  Typography,
  Link as MuiLink,
} from '@mui/material';
import { red } from '@mui/material/colors';
import { PageLayout, getPageStatus } from 'components/PageLayout';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { OrganizationSeatsSummary } from 'features/organizations/components/OrganizationSeatsSummary';
import { useOrganizationDetails } from 'features/organizations/hooks/useOrganizationDetails';
import { useOrganizationState } from 'features/organizations/state/useOrganizationState';
import { OrganizationMembers } from 'features/users/components/OrganizationMembers';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { useAuthState } from 'state/useAuthState';

export const OrganizationDetails = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    permissions: { canManageOrganization },
  } = useAuthState();
  const { details, isLoading } = useOrganizationDetails();
  const location = useLocation<{ fromRoute?: string }>();
  const { resetOrganizationState } = useOrganizationState();

  useEffect(() => {
    // When user lands on the organization details page, reset the organization state
    // that is used in the purchase/upgrade flow.
    resetOrganizationState();
  }, [resetOrganizationState]);

  const returnRoute = location.state?.fromRoute || '/';

  const hasAvailableSeats = details
    ? details.seats.regular.purchased > details.seats.regular.used ||
      details.seats.pro.purchased > details.seats.pro.used
    : false;

  if (!canManageOrganization) {
    return <Redirect to="/" />;
  }

  const status = getPageStatus({ isLoading });

  return (
    <PageLayout
      data-cname="OrganizationDetails"
      status={status}
      headerBarProps={{ title: t('organization.title'), action: returnRoute }}
    >
      <Helmet>
        <title>{t('organization.title')}</title>
      </Helmet>
      <SimplePageLayout disableGutters>
        <Stack spacing={2} padding={2}>
          <Typography variant="h2">
            {t('organizationSeatsSummary.title')}
          </Typography>
          <OrganizationSeatsSummary seats={details?.seats} showUsed />

          {details?.currentUserCanPurchaseSeats &&
            (details?.canPurchaseSeats ? (
              <Button
                size="large"
                fullWidth
                component={Link}
                to="/organization/upgrade"
              >
                {t('organization.manageMemberships')}
              </Button>
            ) : (
              !hasAvailableSeats && (
                <Alert severity="warning" icon={false}>
                  <Trans i18nKey="organization.purchaseContactSales">
                    To puchase additional seats, please contact us at
                    <MuiLink
                      href="mailto:sales@watchduty.org"
                      target="_blank"
                      rel="noreferrer"
                      sx={{ color: red[500] }}
                    >
                      sales@watchduty.org
                    </MuiLink>
                  </Trans>
                </Alert>
              )
            ))}

          <Button
            size="large"
            fullWidth
            component={Link}
            disabled={!hasAvailableSeats}
            to="/organization/members/create"
          >
            {t('organization.addMember')}
          </Button>
        </Stack>
        <OrganizationMembers />
      </SimplePageLayout>
    </PageLayout>
  );
};
